// ─── src/components/dashboard/HorizontalBarChart.tsx ─────────────────────────

import type { FC } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Cell, Tooltip,
  ResponsiveContainer,
} from "recharts";
import type { ChartEntry } from "../../types/stats";
import { CHART_COLORS } from "./Charts";

const CustomTooltip = ({
  active, payload,
}: {
  active?: boolean;
  payload?: { payload: ChartEntry; value: number }[];
}) => {
  if (!active || !payload?.length) return null;
  const { name, value, percentage } = payload[0].payload;
  return (
    <div className="bg-brand-card border border-brand-border rounded-xl
                    px-4 py-3 shadow-xl text-sm">
      <p className="text-white font-semibold capitalize">{name}</p>
      <p className="text-brand-muted mt-0.5">
        {value}
        <span className="text-brand-orange ml-2">({percentage}%)</span>
      </p>
    </div>
  );
};

// Category labels can be long Arabic sentences (e.g. survey priorities like
// "تقديم دعم أكبر في مجال الجاهزية للحصول على التمويل."). Truncate on the axis
// with a visible ellipsis; full text is preserved in the payload so the
// tooltip on hover still shows the complete label.
const MAX_LABEL_CHARS = 34;
const truncateLabel = (v: string) =>
  v.length > MAX_LABEL_CHARS ? v.slice(0, MAX_LABEL_CHARS) + "…" : v;

export const HorizontalBarChart: FC<{ data: ChartEntry[] }> = ({ data }) => {
  const sorted = [...data].sort((a, b) => b.value - a.value).slice(0, 15);

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={sorted}
        layout="vertical"
        margin={{ top: 4, right: 20, bottom: 4, left: 8 }}
      >
        <XAxis
          type="number"
          tick={{ fill: "#9ca3af", fontSize: 11 }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          type="category"
          dataKey="name"
          width={240}
          interval={0}
          tick={{ fill: "#9ca3af", fontSize: 11 }}
          tickFormatter={truncateLabel}
          axisLine={false}
          tickLine={false}
        />
        <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
        <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={22}>
          {sorted.map((_, i) => (
            <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};
